import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";

const defaultPricingPlans = [
  {
    name: "Free",
    price: "$0",
    period: "/mo",
    features: [
      { text: "2 Platform Connections", included: true },
      { text: "3 Days Message History", included: true },
      { text: "5 AI Auto-Send / Month", included: true },
      { text: "10 AI Drafts / Month", included: true },
      { text: "50 AI Summaries / Month", included: true },
      { text: "5 Calendar Events / Month", included: true },
      { text: "1 Active FAQ", included: true },
      { text: "25 Priority Hub Items / Month", included: true },
      { text: "2 Workflows (Max 3 Steps)", included: true },
      { text: "100 Runs / Month", included: true },
      { text: "Voice Transcription", included: false },
      { text: "Priority Support", included: false },
    ],
    cta: "Get Started",
    href: "/login?view=signup",
    popular: false,
  },
  {
    name: "Starter",
    price: "$29",
    period: "/mo",
    features: [
      { text: "Unlimited Platforms", included: true },
      { text: "Unlimited Message History", included: true },
      { text: "100 AI Auto-Send / Month", included: true },
      { text: "1,000 AI Drafts / Month", included: true },
      { text: "Unlimited AI Summaries", included: true },
      { text: "100 Calendar Events / Month", included: true },
      { text: "25 Active FAQs", included: true },
      { text: "500 Priority Hub Items / Month", included: true },
      { text: "30 Workflows (Max 10 Steps)", included: true },
      { text: "5,000 Runs / Month", included: true },
      { text: "Voice Transcription", included: false },
    ],
    cta: "Choose Starter",
    planType: "starter",
    popular: false,
  },
  {
    name: "Growth",
    price: "$59",
    period: "/mo",
    features: [
      { text: "Unlimited Everything", included: true, highlight: true },
      { text: "Unlimited Platforms", included: true },
      { text: "Unlimited AI Auto-Send", included: true },
      { text: "Unlimited AI Drafts", included: true },
      { text: "Unlimited Calendar Sync", included: true },
      { text: "Unlimited Smart FAQs", included: true },
      { text: "Unlimited Priority Hub", included: true },
      { text: "Unlimited Workflows", included: true },
      { text: "Unlimited Steps & Runs", included: true },
      { text: "Voice Transcription", included: true, highlight: true },
      { text: "Priority Support", included: true },
    ],
    cta: "Go Unlimited",
    planType: "growth",
    popular: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    features: [
      { text: "Everything in Growth", included: true, highlight: true },
      { text: "Dedicated Account Manager", included: true },
      { text: "Invoice / Bank Transfer", included: true },
      { text: "Custom Integrations", included: true },
      { text: "Priority Support", included: true },
      { text: "SLA Guarantee", included: true },
    ],
    cta: "Contact Sales",
    planType: "enterprise",
    popular: false,
  },
];

export const PricingSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [pricingPlans, setPricingPlans] = useState(defaultPricingPlans);
  const [enterpriseSeats, setEnterpriseSeats] = useState(5);
  const [entPricePerSeat, setEntPricePerSeat] = useState(39);

  // Fetch geo-pricing on mount
  useEffect(() => {
    fetch('/api/geo-pricing')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        setEntPricePerSeat(data.enterprise || 39);
        setPricingPlans(prev => prev.map(plan => {
          if (plan.planType === 'starter' && data.starter) return { ...plan, price: `$${data.starter}` };
          if (plan.planType === 'growth' && data.growth) return { ...plan, price: `$${data.growth}` };
          if (plan.planType === 'enterprise' && data.enterprise) return { ...plan, price: `$${data.enterprise}`, period: '/seat/mo' };
          return plan;
        }));
      })
      .catch(() => {});
  }, []);

  const checkScroll = () => {
    if (scrollRef.current) {
      // Batch all DOM reads together before any state writes
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const canLeft = scrollLeft > 10;
      const canRight = scrollLeft < scrollWidth - clientWidth - 10;
      // Schedule state updates outside the layout read
      requestAnimationFrame(() => {
        setCanScrollLeft(canLeft);
        setCanScrollRight(canRight);
      });
    }
  };

  useEffect(() => {
    checkScroll();
    // Scroll to Pro Max on load
    setTimeout(() => {
      if (scrollRef.current) {
        const cardWidth = scrollRef.current.querySelector('.pricing-card')?.clientWidth || 0;
        requestAnimationFrame(() => {
          scrollRef.current?.scrollTo({ left: cardWidth + 24, behavior: 'smooth' });
        });
      }
    }, 500);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.querySelector('.pricing-card')?.clientWidth || 300;
      const scrollAmount = direction === 'left' ? -cardWidth - 24 : cardWidth + 24;
      requestAnimationFrame(() => {
        scrollRef.current?.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      });
    }
  };

  const handlePlanSelect = (planType?: string, href?: string) => {
    if (planType === 'enterprise') {
        localStorage.setItem('clearmakes_pending_plan', planType);
        localStorage.setItem('clearmakes_pending_seats', enterpriseSeats.toString());
        window.location.href = `/login?view=signup&plan=${planType}`;
        return;
    }
    if (href) {
      window.location.href = href;
    } else if (planType) {
      localStorage.setItem('clearmakes_pending_plan', planType);
      window.location.href = `/login?view=signup&plan=${planType}`;
    }
  };

  return (
    <section id="pricing" className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 noise-texture" />
      
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-gradient-to-br from-accent/10 via-primary/5 to-transparent rounded-full blur-3xl" />

      <div className="container relative" ref={ref}>
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="heading-lg mb-4">Choose Your Plan</h2>
          <p className="body-lg max-w-xl mx-auto">
            Start for free. Upgrade as you grow. No hidden fees.
          </p>
        </motion.div>

        <div className="relative px-12 md:px-16">
          {/* Navigation Arrows */}
          <button
            onClick={() => scroll('left')}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full glass-panel flex items-center justify-center transition-all ${
              canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-label="Previous"
          >
            <ChevronLeft size={20} />
          </button>
          
          <button
            onClick={() => scroll('right')}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full glass-panel flex items-center justify-center transition-all ${
              canScrollRight ? 'opacity-100 animate-pulse-glow' : 'opacity-0 pointer-events-none'
            }`}
            aria-label="Next"
          >
            <ChevronRight size={20} />
          </button>

          {/* Cards Container */}
         {/* Cards Container */}
          <div 
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto hide-scrollbar py-8 snap-x snap-mandatory"
            onScroll={checkScroll}
          >
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.name}
                // UPDATED: Added 'relative' and the shadow styling
                className={`pricing-card relative flex-shrink-0 w-[calc(100%-2rem)] sm:w-[320px] snap-center rounded-2xl ${
                  plan.popular ? 'shadow-[0_0_25px_-5px_hsl(var(--primary)/0.4)]' : ''
                }`}
                initial={{ opacity: 0, y: 60 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                {/* NEW: Absolute Gradient Border Layer (Expands 1px outwards) */}
                {plan.popular && (
                  <div className="absolute -inset-[1px] rounded-[17px] bg-gradient-to-b from-primary to-accent z-0" />
                )}

                <div
                  // UPDATED: Added z-10, w-full, rounded-2xl and swapped background to match Pricing.tsx
                  className={`relative z-10 h-full w-full p-6 flex flex-col rounded-2xl ${
                    plan.popular ? 'bg-[#0A0F1C]' : 'glass-panel-hover'
                  }`}
                >
                  {plan.popular && (
                    // UPDATED: Added z-20 so the badge overlaps the new border correctly
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-primary to-accent rounded-full text-xs font-semibold text-white z-20">
                      Most Popular
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className={`text-xl font-semibold mb-2 ${plan.popular ? 'gradient-text-primary' : ''}`}>
                      {plan.name}
                    </h3>
                    <p className="text-muted-foreground text-sm">{plan.period}</p>
                    <p className="text-4xl font-bold my-4">{plan.price}</p>
                  </div>

                  {/* Rest of the card features and button... */}
                  <ul className="space-y-3 text-sm flex-grow">
                    {plan.features.map((feature) => (
                      <li key={feature.text} className="flex items-center">
                        {feature.included ? (
                          <Check className="w-4 h-4 mr-2 text-green-500" />
                        ) : (
                          <X className="w-4 h-4 mr-2 text-red-500" />
                        )}
                        <span className={feature.highlight ? 'font-semibold text-white' : ''}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8">
                    {plan.planType === 'enterprise' && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-muted-foreground">Seats: <strong className="text-white">{enterpriseSeats}</strong></span>
                          <span className="text-xs text-primary font-medium bg-primary/10 px-2 py-0.5 rounded-full">${enterpriseSeats * entPricePerSeat}/mo</span>
                        </div>
                        <label htmlFor="enterprise-seats-slider" className="sr-only">Number of seats</label>
                        <input
                          id="enterprise-seats-slider"
                          type="range"
                          min="5"
                          max="10000"
                          step="1"
                          value={enterpriseSeats}
                          onChange={(e) => setEnterpriseSeats(parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary"
                          aria-label="Number of seats"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>5</span>
                          <span>5,000</span>
                          <span>10,000</span>
                        </div>
                      </div>
                    )}
                    <Button
                      // OPTIONAL: If you want the button to match Pricing.tsx exactly as well
                      variant={plan.popular ? "hero" : "default"}
                      className="w-full"
                      onClick={() => handlePlanSelect(plan.planType, plan.href)}
                    >
                      {plan.cta}
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Enterprise Slider */}
      </div>
    </section>
  );
};
